import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Button, 
  Space, 
  Input, 
  Radio, 
  Table, 
  Tag,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminWordbookApi } from '@/services/api';
import { formatNumber } from '@/utils/number';

const { Title } = Typography;
const { Search } = Input;
const DEFAULT_PAGE_SIZE = 10;

interface WordbookData {
  key: string;
  no: number;
  wordbookName: string;
  wordCount: number;
  isPublic: boolean;
  registrant: string;
  registrationDate: string;
}

const AdminWordbookManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') || '';
  const initialStatus = searchParams.get('pub') || 'all';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(false);
  const [filteredWordbooks, setFilteredWordbooks] = useState<WordbookData[]>([]);
  const [searchText, setSearchText] = useState(initialKeyword);
  const [selectedPublicStatus, setSelectedPublicStatus] = useState<string>(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);

  // 테이블 컬럼 정의
  const columns: ColumnsType<WordbookData> = [
    {
      title: '순번',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center',
      render: (no: number) => formatNumber(no),
    },
    {
      title: '단어장명',
      dataIndex: 'wordbookName',
      key: 'wordbookName',
      width: 300,
      align: 'left'
    },
    {
      title: '단어갯수',
      dataIndex: 'wordCount',
      key: 'wordCount',
      width: 120,
      align: 'left',
      render: (count: number) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {formatNumber(count)}개
        </span>
      )
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 150,
      align: 'left'
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 150,
      align: 'left',
      render: (date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('ko-KR');
      }
    }
  ];

  // 검색 및 필터링 함수 (서버 호출)
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handlePublicStatusChange = (value: string) => {
    setSelectedPublicStatus(value);
    setPage(1);
  };

  // 테이블 행 클릭 이벤트
  const handleRowClick = (record: WordbookData) => {
    navigate(
      `/admin/wordbook/detail/${record.key}`,
      { state: { from: `${location.pathname}${location.search}` } },
    );
  };

  const syncSearchParams = useCallback((nextPage: number, nextSize: number, keyword: string, pub: string) => {
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (pub && pub !== 'all') params.set('pub', pub);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [setSearchParams]);

  const loadList = useCallback(async (pageIndex = 1, qValue: string = searchText, publicStatus: string = selectedPublicStatus, size = pageSize) => {
    setLoading(true);
    try {
      const params: any = { q: qValue, page: pageIndex - 1, size };
      if (publicStatus !== 'all') params.isPublic = (publicStatus === 'public');
      const res: any = await adminWordbookApi.list(params);
      if (res?.success) {
        const { content = [], totalElements = 0 } = res.data || {};
        const mapped: WordbookData[] = content.map((it: any, idx: number) => ({
          key: String(it.id),
          no: totalElements - ((pageIndex - 1) * size + idx),
          wordbookName: it.title,
          wordCount: (typeof it.itemCount === 'number' ? it.itemCount : 0),
          isPublic: it.isPublic ?? true,
          registrant: it.registrant || '-',
          registrationDate: (it.createdAt || '').split('T')[0] || ''
        }));
        setFilteredWordbooks(mapped);
        setTotal(totalElements);
        setPage(pageIndex);
        setPageSize(size);
      }
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedPublicStatus, pageSize]);

  useEffect(() => {
    loadList(page, searchText, selectedPublicStatus, pageSize);
    syncSearchParams(page, pageSize, searchText, selectedPublicStatus);
  }, [page, pageSize, searchText, selectedPublicStatus, loadList, syncSearchParams]);

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>단어장 목록</Title>
        </div>

        {/* 검색 및 필터 영역 */}
          <Row gutter={24} align="middle" style={{ marginBottom: 16 }}>
            <Col span={8}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>단어장명 / 등록자 검색</div>
              <Search
                placeholder="단어장명 또는 등록자를 입력하세요"
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={handleSearch}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </Col>
          </Row>

          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>   
          <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => navigate('/admin/wordbook/create')}
              size="large"
            >
              새 단어장 등록
            </Button>

          </div>

        {/* 테이블 */}
          <Table
            columns={columns}
            dataSource={filteredWordbooks}
            loading={loading}
            pagination={{
              current: page,
              total: total || filteredWordbooks.length,
              pageSize: pageSize,
              showTotal: (total, range) => {
                if (!total) return `0-0 / 총 0개`;
                const start = Math.max(total - range[1] + 1, 1);
                const end = Math.max(total - range[0] + 1, 1);
                return `${formatNumber(start)}-${formatNumber(end)} / 총 ${formatNumber(total)}개`;
              },
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            size="middle"
            scroll={{ x: 1000 }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            onChange={(pagination) => {
              const nextPage = pagination.current || 1;
              const nextSize = pagination.pageSize || pageSize;
              setPage(nextPage);
              setPageSize(nextSize);
            }}
          />
      </div>
    </AdminLayout>
  );
};

export default AdminWordbookManagement; 